import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';

export const SplitView: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [pageRange, setPageRange] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSplit = async () => {
    if (!file) {
      setError("Please select a PDF file.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const totalPages = pdf.getPageCount();
      
      const newPdf = await PDFDocument.create();
      
      let pagesToExtract: number[] = [];
      if (!pageRange.trim()) {
        pagesToExtract = Array.from({length: totalPages}, (_, i) => i);
      } else {
        const ranges = pageRange.split(',');
        for (const r of ranges) {
          const parts = r.trim().split('-');
          if (parts.length === 1) {
            const page = parseInt(parts[0], 10) - 1;
            if (page >= 0 && page < totalPages) pagesToExtract.push(page);
          } else if (parts.length === 2) {
            const start = parseInt(parts[0], 10) - 1;
            const end = parseInt(parts[1], 10) - 1;
            for (let i = start; i <= end; i++) {
              if (i >= 0 && i < totalPages) pagesToExtract.push(i);
            }
          }
        }
      }
      
      if (pagesToExtract.length === 0) {
        throw new Error("No valid pages selected for extraction.");
      }
      
      const copiedPages = await newPdf.copyPages(pdf, pagesToExtract);
      copiedPages.forEach((page) => newPdf.addPage(page));
      
      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'split.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Split PDF</h2>
          <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Enter ranges separated by commas (e.g. &quot;1-3, 5, 7-10&quot;). Each range will be extracted as a separate file.
          </p>
        </div>
        <button onClick={() => { void handleSplit(); }} disabled={isProcessing} style={{ padding: '0.5rem 1rem', background: 'var(--color-accent)', color: 'var(--color-on-accent)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
          {isProcessing ? 'Splitting...' : 'Split PDF'}
        </button>
      </div>
      {error && <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', background: '#ffebee', borderRadius: '4px' }}>{error}</div>}
      
      <div style={{ flex: 1, border: '2px dashed var(--color-border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <input 
          type="file" 
          accept="application/pdf" 
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setFile(file);
            }
          }}
          style={{ marginBottom: '1rem' }}
        />
        {file && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <span>{file.name}</span>
            <input 
              type="text" 
              placeholder="e.g. 1, 3, 5-7" 
              value={pageRange}
              onChange={(e) => { setPageRange(e.target.value); }}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', width: '250px' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
